import React, { Dispatch, SetStateAction } from "react"
import { InputKlarnaCheckoutConfig } from "lib/types/generated/graphql-types"
import dayjs from "dayjs"
import styled from "styled-components"
import { useAppDispatch } from "lib/store"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { SubmitHandler, useForm } from "react-hook-form"
import { useMutation, useQuery } from "@apollo/client"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { PROVIDER_CATEGORY, PROVIDER_TYPE } from "lib/types/common"

import DELETE_KUSTOM_CONFIG from "../../../../graphql/mutations/config/DeleteKustomConfig"
import CREATE_OR_UPDATE_KUSTOM_CONFIG from "../../../../graphql/mutations/config/CreateOrUpdateKustomConfig"
import GET_KUSTOM_CONFIG from "graphql/queries/config/GetKustomCheckoutConfig"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import DeleteButton from "components/Ui/EditSidebar/DeleteButton"
import { KustomCheckoutConfigInputs } from "components/Config/KustomCheckout/KustomCheckoutConfigInputs"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import ConnectedStoreMarket from "components/Config/ConnectedStoreMarkets/ConnectedStoreMarket"

import { ReactComponent as KustomLogo } from "images/providers/kustom-logo.svg"
import { WithAuth } from "../../../../components/Auth"
import { PermissionGroups } from "../../../../routes/constants"

const Label = styled.label`
  font-weight: 700;
  display: flex;
  align-items: center;
  padding-bottom: 0.3rem;
`

const Info = styled.div`
  padding-bottom: 2rem;
`

interface ExtendedInputKlarnaConfig extends InputKlarnaCheckoutConfig {
  id: string
}

type Props = {
  refetch: () => void
  editId: string
  setEditId: Dispatch<SetStateAction<string>>
}

function EditKustomConfig({ refetch, editId, setEditId }: Props) {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<ExtendedInputKlarnaConfig>({ mode: "onBlur" })

  const { data, loading } = useQuery(GET_KUSTOM_CONFIG, {
    variables: { id: editId }
  })

  const kustomConfig = data?.getKlarnaCheckoutConfig

  const dispatch = useAppDispatch()
  const [updateKlarnaConfig, { loading: updateLoading }] = useMutation(
    CREATE_OR_UPDATE_KUSTOM_CONFIG,
    {
      onCompleted: () => {
        dispatch(
          alertActions.actions.createAlert({
            message: "Kustom checkout configuration successfully updated",
            type: "success"
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )

  const onSubmit: SubmitHandler<ExtendedInputKlarnaConfig> = (data) => {
    updateKlarnaConfig({ variables: { ...data, id: kustomConfig.id } })
  }

  const closeSidebar = () => {
    setEditId("")
    dispatch(hideEditSidebar())
  }
  if (loading) return <EditSidebar loading={loading} />
  return (
    <EditSidebar setEditId={setEditId}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <EditSidebarHeader
          icon={<KustomLogo/>}
          title={kustomConfig?.merchantId}
          id={kustomConfig?.id}
          cancelEvent={() => closeSidebar()}
        >
          <WithAuth requiredGroups={PermissionGroups.providers.write}>
            <DeleteButton
              id={kustomConfig?.id}
              name={kustomConfig?.merchantId}
              entity={"Kustom checkout configuration"}
              redirectUrl="/settings/providers/kustom"
              mutation={DELETE_KUSTOM_CONFIG}
              refetch={refetch}
              postDeleteFunction={() => setEditId("")}
              secureDelete={true}
            />
            <PrimaryButton type="submit" loading={updateLoading}>
              Save
            </PrimaryButton>
          </WithAuth>
        </EditSidebarHeader>
        <Tabs>
          <TabList>
            <Tab>General</Tab>
            <Tab>Connected store markets</Tab>
          </TabList>
          <TabPanel>
            <KustomCheckoutConfigInputs
              control={control}
              errors={errors}
              setValue={setValue}
              defaultValues={kustomConfig}
            />
            <Label>Created:</Label>
            <Info>{dayjs(kustomConfig.created).format("YYYY-MM-DD")}</Info>
            <Label>Updated:</Label>
            <Info>{dayjs(kustomConfig.updated).format("YYYY-MM-DD")}</Info>
          </TabPanel>
          <TabPanel>
            <ConnectedStoreMarket
              providerId={kustomConfig.id}
              providerType={PROVIDER_TYPE.KLARNA_CHECKOUT}
              providerCategory={PROVIDER_CATEGORY.PAYMENT}
            />
          </TabPanel>
        </Tabs>
      </form>
    </EditSidebar>
  )
}

export default EditKustomConfig
